// =============================================================================
// ui/tabs/Dashboard.tsx — the landing screen.
// -----------------------------------------------------------------------------
// Ported next rather than Journal because it is the FALLBACK_TAB — the first
// screen anyone sees, and until now it said "Not ported yet" — and because it is
// entirely read-only, so deferring writes costs it nothing. Journal is the
// largest tab and the first that writes; porting its read side alone would leave
// a journal you cannot write in.
//
// ── 🚨 TODAY'S P&L IS NOT SCOPED TO THE RANGE FILTER ──
//
// The old client is explicit about this and it is easy to get wrong:
//
//   "Always reflects today, regardless of the global date range filter — the
//    Today's P&L widget is conceptually about today, not the filtered period."
//
// So this tab issues TWO reads: one for the active range (everything else on the
// screen) and one pinned to today. Feeding the widget from the range-scoped rows
// would show $0 whenever the user filters to any period not containing today —
// which, since the default range is `week`, would look right almost always and
// be wrong exactly when someone checks it after switching to last month.
//
// "Today" is the SESSION day in session mode, which is what `useRange().today`
// already resolves.
//
// ── ⚠ PARTIAL — AND THIS HEADER NO LONGER LISTS WHAT IS MISSING ──
//
// It used to, and it was WRONG within a day of every landing: it named the equity
// curve, the gauge row, the weekday/session breakdowns and Top Patterns as
// unported long after all four shipped. The on-screen card said the same thing,
// so Josh read a stale claim in the running app.
//
// That is the D68 rot exactly — a sentence about what the client can do goes
// stale silently, because nothing renders it and nothing tests it. Trades.tsx
// already learned this and states a rule instead. So does this:
//
//   **Every population on this tab is transcribed from the beta function that
//   owns that card, and NO two cards may share a memo by assumption.**
//
// Three distinct sets are live here today — `scoped` (countsForMetrics),
// `forPatterns` (selection only), and the KPI strip's internal scoping — because
// beta genuinely uses all three on one screen. That is asserted by tests that
// name each rule; see D77 and D80.
//
// What is still absent is named ON SCREEN, in one card, which is the only copy
// a user reads and therefore the only one worth maintaining.
// =============================================================================

import { useEffect, useMemo, useCallback } from 'react';
import { useSnapshot } from '../state/snapshot';
import { useShare } from '../state/share';
import { activeAccountLabel } from '../../selectors/share';
import { useDemo } from '../state/demo';

/** The id beta uses, kept so the two clients' capture regions are comparable. */
const SNAPSHOT_REGION_ID = 'dash-snapshot-region';
import { useAccounts } from '../state/accounts';
import { useContracts } from '../state/contracts';
import { usePreferences } from '../state/preferences';
import { usePnlUnit } from '../state/usePnlUnit';
import { tradeValue } from '../../selectors/pnlUnit';
import type { PnlUnit } from '../../selectors/pnlUnit';
import { useActiveFacets, useFilters } from '../state/filters';
import { useRange } from '../state/range';
import { useTradeRows } from '../state/useTradeRows';
import { useFacetUniverse } from '../state/useFacetUniverse';
import { GetStarted } from './GetStarted';
import type { AccountsIntent } from './Accounts';
import type { TradesIntent } from '../shell/TradeActions';
import { selectKpis, type Kpis } from '../../selectors/kpis';
import { effectiveTradeDate } from '../../selectors/population';
import {
  applyFacets, tradesCountingForMetrics, tradesForSelection, type Account,
} from '../../selectors/accounts';
import { usePatterns } from '../state/patterns';
import {
  sessionPnl, topPatterns, weekdayPnl,
  type TopPatternRow,
} from '../../selectors/dashBreakdowns';
import {
  disciplineScore, equitySeries, expectancyR, profitFactorGauge,
  riskAdjustedWinRate, targetEfficiency,
  type Gauge as GaugeData, type GaugeTrade,
} from '../../selectors/dashGauges';
import { buildHeatmap } from '../../selectors/heatmap';
import { dashAdjustmentLegs, dashAdjustmentTotal } from '../../selectors/dashAdjustments';
import { mfeCapture, peakCapture, type CaptureStat, type PeakTrade } from '../../selectors/capture';
import { useGoals } from '../state/useGoals';
import {
  goalAccountLabel, goalMetricsGate, goalProgress, visibleGoals,
  type Goal, type GoalProgress,
} from '../../selectors/goals';
import { EquityCurve } from '../charts/EquityCurve';
import { Heatmap } from '../charts/Heatmap';
import { BarCard } from './BarCard';
import { TradeTable, makeAccountLabeller } from './TradeTable';
import { TRADE_TABLE_COLUMNS_DEFAULT } from './tradeColumns';
import { Gauge } from '../charts/Gauge';
import type { TradeTableRow } from '../../io/sync/trades';
import { fullDayLabel, money, num, pct, pnlClass, unitValue } from '../format';


/**
 * The unit as a WORD, for a sentence. `PNL_UNIT_LABELS` is menu copy
 * (“Dollars ($)”, “R-multiple”) and reads wrong mid-sentence.
 */
const UNIT_WORD: Record<PnlUnit, string> = {
  usd: 'dollars', points: 'points', ticks: 'ticks', r: 'R',
};

/**
 * @param go  Send the reader to another tab, optionally pre-arming the Trades
 *            tab. Supplied by `App` because the cold-start banners (#46) are the
 *            first thing a new user can act on, and a call to action that cannot
 *            navigate is decoration.
 */
export function DashboardTab(
  { go }: { go: (tab: string, intent?: TradesIntent | AccountsIntent) => void },
) {
  const { bounds, mode, today, label, captureLabel, wallClockNow } = useRange();
  const { accounts } = useAccounts();
  // ⚠ SAFE WITHOUT A PROVIDER — `useDemo` returns "not in a demo" rather than
  // throwing, which is why adding it here does not oblige every Dashboard test
  // to mount one. See `ui/state/demo.tsx`.
  const demo = useDemo();
  /**
   * ⚠ ALL-TIME, for the cold-start banners — "have you ever traded" is a
   * whole-book question and the range read cannot answer it. Already fetched by
   * the toolbar’s filter bar on every tab, so this costs no extra request.
   */
  const { rows: universe } = useFacetUniverse();
  const { patterns } = usePatterns();
  const { accounts: selection } = useFilters();
  // The global display unit — $ / points / ticks / R. See `UnitPicker`.
  const { unit } = usePnlUnit();

  // ⚠ `useActiveFacets`, never `storedFacets` — a pattern or tag id that
  // outlived its registry matches no trade, so the raw selection empties the
  // whole Dashboard with the drawer showing nothing ticked.
  const { facets } = useActiveFacets();
  const { contracts } = useContracts();
  /**
   * One trade’s value in the display unit, for the table’s P&L column.
   *
   * ⚠ The table takes this FUNCTION rather than the contract catalogue — it has
   * no other reason to know what a tick size is. Null where the trade has no
   * value in the unit (no stop, no R), which the cell renders as an em-dash.
   */
  const valueOf = useCallback(
    // 🚨 NULL IN, NULL OUT. `tradeValue` coerces a null P&L to 0 because that is
    // right for SUMMING — a trade with no result contributes nothing. It is wrong
    // for a CELL: an open or unpriced trade would render $0.00 and read as a
    // scratch, which is a different and confident claim. Caught by the table’s
    // own test, which has asserted the em-dash since the port began.
    (t: TradeTableRow) => (t.netPnl == null ? null : tradeValue(t as never, unit, contracts ?? [])),
    [unit, contracts],
  );
  const { prefs } = usePreferences();

  /**
   * ⚠ Its own read, deliberately outside the range filter. See `useGoals`.
   *
   * ⚠ AND ITS OWN CLOCK, shared with the range rather than minted here.
   * `useGoals` has always taken an injectable `now` — its docstring says why:
   * "a test that reads the machine's clock asserts a different window every day
   * it runs" — and this call site was not passing one. The Goals tests duly
   * passed all week and failed on the Sunday the weekly window rolled over.
   */
  const goalsState = useGoals(wallClockNow);

  /**
   * Register this tab as what the header camera captures.
   *
   * ⚠ Registered by the TAB, cleared on unmount. The button is in the shell and
   * has no way to know which tabs are capturable — beta hardcodes a three-way
   * `if (currentTab === …)` in its click handler, which is the line that gets
   * forgotten when a fourth capturable tab lands. Here a tab opts in by saying
   * so, and the button greys out when nothing has.
   */
  const snapshot = useSnapshot();
  const { setTarget } = snapshot;
  useEffect(() => {
    setTarget({ regionId: SNAPSHOT_REGION_ID, title: label, label: 'the dashboard' });
    return () => setTarget(null);
  }, [setTarget, label]);

  /**
   * 🚨 AND WHAT A PUBLIC LINK TO IT WOULD CONTAIN.
   *
   * ⚠ NO TRADE IDS, which is beta's behaviour and not an omission:
   * `shareDashboard()` passes no trades (app/index.html:29511), so a shared
   * dashboard is the summary picture and nothing else. The Recent Trades table
   * is part of the CAPTURE, so it appears as it looked — but no per-trade data
   * is serialised, and nothing in it can be opened.
   */
  const { setSource } = useShare();
  const shareAccountLabel = activeAccountLabel(
    (accounts ?? []).map((a) => ({ id: String(a.id), name: a.name })),
    selection ?? [],
  );
  useEffect(() => {
    setSource({
      kind: 'dashboard', title: 'Dashboard', subtitle: label,
      accountLabel: shareAccountLabel, tradeIds: [],
      // ⚠ The unit still travels with no trades: it describes the PICTURE too,
      // and a viewer reading the payload should not have to infer it.
      unit, valueById: {},
    });
    return () => setSource(null);
  }, [setSource, label, shareAccountLabel, unit]);

  /** ⚠ The SAME labeller the Trades tab uses, so an account renders identically
      on both screens rather than as an id on one of them. */
  const labelAccount = useMemo(() => makeAccountLabeller(accounts ?? []), [accounts]);

  /**
   * The Pattern column's resolver (#45).
   *
   * ⚠ An UNRESOLVED id renders EMPTY, not the id. A pattern deleted after the
   * trade was tagged leaves the id behind, and printing `pat_8f3a…` in a review
   * column reads as data corruption rather than as a pattern that no longer
   * exists. Beta's accessor does the same — `find(...)?.name || ''`.
   */
  const labelPattern = useMemo(() => {
    const byId = new Map((patterns ?? []).map((p) => [p.id, p.name]));
    return (t: TradeTableRow) => (t.patternId ? byId.get(t.patternId) ?? '' : '');
  }, [patterns]);

  // Read 1 — the active range. Everything except the Today widget.
  const ranged = useTradeRows(bounds, mode);

  // Read 2 — TODAY, deliberately ignoring the range filter. See the header.
  const todayBounds = useMemo(
    () => (today ? { startDate: today, endDate: today } : null),
    [today],
  );
  const todays = useTradeRows(todayBounds, mode);

  // ⚠ Facets narrow the RANGE numbers, not the Today widget — see below.
  const inScope = useMemo(
    () => (ranged.rows && mode ? applyFacets(ranged.rows, facets, mode) : null),
    [ranged.rows, facets, mode],
  );

  const kpis = useMemo<Kpis<TradeTableRow> | null>(() => {
    if (!inScope || !accounts || !contracts) return null;
    return selectKpis({
      trades: inScope,
      accounts: accounts as readonly Account[],
      contracts,
      selection,
      unit,
    });
  }, [inScope, accounts, contracts, selection, unit]);

  const todayKpis = useMemo<Kpis<TradeTableRow> | null>(() => {
    if (!todays.rows || !accounts || !contracts) return null;
    return selectKpis({
      trades: todays.rows,
      accounts: accounts as readonly Account[],
      contracts,
      selection,
      unit,
    });
  }, [todays.rows, accounts, contracts, selection, unit]);

  /**
   * ── 🚨 THE DASHBOARD POPULATION, AND FIXING A DEFECT IN THE SHIPPED ONE ──
   *
   * Beta's dashboard is `tradesForActive()` — the active range AND the account
   * selection — with `countsForMetrics` applied at every card. `inScope` above
   * is only the FACETED rows: it applies neither the account selection nor the
   * exclude-from-metrics flag.
   *
   * The KPI strip was already right, because `selectKpis` scopes internally. The
   * RECENT TRADES list was not: it listed trades from every account regardless of
   * the account filter, and included SIM and market-replay rows that beta's
   * `countsForMetrics` drops. Selecting one account showed six trades from
   * another, and nothing on screen said why.
   *
   * ⚠ It survived because the filter chip reads "All accounts" by default, where
   * selection-scoped and unscoped agree exactly. The same shape as every other
   * defect in this port — an assertion, or in this case a screenshot, of a case
   * that cannot expose the bug.
   */
  const scoped = useMemo(() => {
    if (!inScope || !accounts) return null;
    return tradesCountingForMetrics(inScope, selection, accounts as readonly Account[]);
  }, [inScope, accounts, selection]);

  /**
   * ── 🚨 SORTED BY THE RAW DATE, NOT THE SESSION DATE (#50) ──
   *
   * Beta's line is `b.core.date + ' ' + timeIn` — the date the trade actually
   * happened on. This used `effectiveTradeDate`, and in session mode the two
   * disagree: an 18:40 trade on the 27th belongs to the 28th's session, so
   * sorting by session date promoted it above the 28th's morning trades. With
   * only six slots the two clients then DROPPED A DIFFERENT TRADE — same count,
   * same order on screen, different membership.
   *
   * ⚠ Third instance of the day-attribution defect; it has shipped on Trades and
   * on Calendar already. It survived here because **every test in this file ran
   * in calendar mode**, where the raw date and the session date are identical
   * and both rules pick the same six. A fixture that cannot express a bug is not
   * coverage.
   *
   * ⚠ THE DIVERGENCE IS RESOLVED TOWARD BETA, and there is a case for the other
   * answer: "recent" arguably means what happened most recently, which is the
   * raw date anyway — beta's rule reads as the more defensible one here. Either
   * way parity is the gate for this phase, and a client that quietly disagrees
   * with beta about which six trades to show is the thing the differential
   * harness exists to catch. Recorded rather than chosen silently.
   *
   * ⚠ `mode` is no longer read here. It stays in the guard because the tab must
   * not render a half-resolved population, which is the same reason every other
   * memo on this screen waits for it.
   */
  const recent = useMemo(() => {
    if (!scoped || !mode) return null;
    return [...scoped]
      .sort((a, b) =>
        `${b.date} ${b.timeIn ?? ''}`.localeCompare(`${a.date} ${a.timeIn ?? ''}`))
      .slice(0, 6);
  }, [scoped, mode]);

  /**
   * ── 🚨 TOP PATTERNS RUNS ON A DIFFERENT SET FROM THE TWO CARDS BESIDE IT ──
   *
   * Beta's weekday and session cards each open with `if (!countsForMetrics(t))
   * continue;`. Top Patterns does not — it is `patternStats(p.id, trades, unit)`
   * over `tradesForActive()`, which is account-selection + range + facets and
   * NOTHING ELSE, and `patternStats` then adds `annotated !== false`.
   *
   * So on ONE screen, two cards drop exclude-from-metrics accounts and the third
   * keeps them. That is not a reading of the source — it was caught by putting
   * the ported card beside Josh's screenshot on the same book:
   *
   *   VA REVERSION   beta +$207.50   ported +$78.82
   *   VALUE PDC      beta  +$85.29   ported absent
   *
   * while ORDERFLOW REVERSAL, DEALER PUMP and VWAP REVERSION matched to the cent
   * — because those three have no trades on his excluded accounts, so the two
   * populations coincide for them and differ for the rest. Self-consistent,
   * plausible, and wrong on three of five rows.
   *
   * ⚠ Fourth occurrence of D77, and the third caught only by a side-by-side.
   */
  const forPatterns = useMemo(() => {
    if (!inScope || !accounts) return null;
    return tradesForSelection(inScope, selection, accounts as readonly Account[]);
  }, [inScope, accounts, selection]);

  /**
   * ── 🚨 THE EQUITY CURVE AND THE GAUGES ALSO SPLIT, THE SAME WAY ──
   *
   * `dashEquityPoints` opens with `trades.filter(countsForMetrics)`. The four
   * gauge aggregates do NOT — `dashRiskAdjWinRate` and `dashDisciplineScore`
   * filter only on their own field, and expectancy-R and target efficiency are
   * computed inline from `trades` directly. All four therefore run on the looser
   * `tradesForActive()` set, exactly like Top Patterns.
   *
   * Caught the same way, on the same screen. The first version put all of them on
   * `scoped` and read:
   *
   *   risk-adj win rate   beta 28% over 232   ported 27% over 218
   *   expectancy (R)      beta 0.29R          ported 0.19R
   *   discipline          beta 99% / 466      ported 98% / 393
   *   target efficiency   beta 14%            ported 9%
   *
   * while WIN RATE and PROFIT FACTOR matched exactly — those two come from
   * `kpis`, which scopes internally and correctly. Four wrong, two right, nothing
   * on screen out of place.
   *
   * ⚠ Fifth D77 occurrence, and the second on this tab alone. The rule now has a
   * shape worth stating: on this dashboard, anything computed by `computeKpis` or
   * by the equity builder is countsForMetrics-scoped, and everything else is not.
   */
  /**
   * ── #41 — THE MONEY THAT IS NOT A TRADE ──
   *
   * Starting balances, payouts, deposits, withdrawals and corrections. Beta adds
   * them to the DISPLAYED total and steps the equity curve on each one; this tab
   * read them nowhere, so a prop payout did not appear on the dashboard at all
   * and the two clients printed different headline numbers for the same week.
   *
   * ⚠ SCOPED BY `selection`, NOT by `scoped`. Adjustments belong to accounts,
   * not to trades — there is no trade to carry the flag — so the account filter
   * is applied directly. Which also means an adjustment survives a facet filter
   * that would drop every trade beside it, exactly as in beta.
   */
  const adjLegs = useMemo(
    () => dashAdjustmentLegs(accounts ?? [], selection, bounds),
    [accounts, selection, bounds],
  );
  const adjTotal = useMemo(() => dashAdjustmentTotal(adjLegs), [adjLegs]);
  /**
   * The adjustment total AS PRINTED — zero unless the unit is dollars.
   *
   * 🚨 Adding a dollar payout to a points total produces a number that is
   * neither, and then prints it as points. Beta guards it with the same test.
   */
  const shownAdj = unit === 'usd' ? adjTotal : 0;

  const chart = useMemo(() => {
    if (!scoped || !forPatterns || !mode || !kpis) return null;
    const legs = [
      ...scoped.map((t) => ({
        date: effectiveTradeDate(t, mode),
        time: t.timeIn ?? '',
        delta: t.netPnl ?? 0,
        kind: 'trade' as const,
        // ⚠ For the hover readout (#47). A field carry, not a derivation.
        contract: t.contract ?? '',
      })),
      // ⚠ Adjustments have NO time, which is what puts them ahead of the day's
      // trades — a carry-in must raise the line before the first trade moves it.
      ...adjLegs.map((a) => ({
        date: a.date, time: '', delta: a.amount,
        kind: 'adj' as const, label: a.label, note: a.note,
      })),
    ];
    // ⚠ The LOOSER set for the gauges. See above.
    const g = forPatterns as unknown as readonly GaugeTrade[];
    return {
      equity: equitySeries(legs),
      winRate: { value: kpis.count ? kpis.winRate : null, arcPct: kpis.winRate, sample: kpis.count },
      riskAdj: riskAdjustedWinRate(g),
      expR: expectancyR(g),
      discipline: disciplineScore(g),
      pf: profitFactorGauge(kpis.profitFactor, kpis.count),
      targetEff: targetEfficiency(g),
    };
  }, [scoped, forPatterns, mode, kpis, adjLegs]);

  /**
   * The Day × Hour heatmap.
   *
   * ⚠ `forPatterns` — the LOOSER set — because beta hands `renderHeatmap` the
   * output of `tradesForActive()` and the renderer's only filter is "has a date
   * and a time-in". Third distinct population on this screen, and beta's (D80).
   */
  const heatmap = useMemo(() => {
    if (!forPatterns || !mode) return null;
    return buildHeatmap(forPatterns.map((t) => ({
      effectiveDate: effectiveTradeDate(t, mode),
      timeIn: t.timeIn,
      netPnl: t.netPnl,
    })));
  }, [forPatterns, mode]);

  /**
   * ── 🚨 TWO CELLS IN THE STAT STRIP RUN ON A DIFFERENT SET FROM THE OTHER SIX ──
   *
   * Sixth D77 occurrence, and the first INSIDE one row of tiles rather than
   * between neighbouring cards. Everything else in `KpiGrid` comes from `kpis`,
   * which applies `countsForMetrics` internally. Beta hands both capture
   * aggregates `tradesForActive()` raw:
   *
   *     const mfeCap  = dashMfeCapture(trades);
   *     const peakCap = dashPeakCapture(trades);
   *
   * So Avg R:R drops trades on excluded accounts and MFE Capture beside it does
   * not. Deliberate on beta's side and kept: these two are about EXIT MECHANICS,
   * where a SIM fill teaches the same lesson as a funded one.
   *
   * ⚠ `forPatterns`, therefore — passing `scoped` compiles and renders and is
   * wrong by a few percent, which is exactly how the Top Patterns and gauge
   * defects survived until a side-by-side caught them.
   */
  const capture = useMemo(() => {
    if (!forPatterns || !contracts) return null;
    // ⚠ Projected FIELD BY FIELD rather than passed whole (D78). The row type is
    // wider than the aggregate's input, and handing over the record is what let
    // an earlier "the editor gets the full object" control pass against code
    // that was already narrowing.
    const rows: PeakTrade[] = forPatterns.map((t) => ({
      points: t.points,
      mfePoints: t.mfePoints,
      riskPoints: t.riskPoints,
      contract: t.contract,
      side: t.side,
      entry: t.entry,
      timeIn: t.timeIn,
      mfeSecs: t.mfeSecs,
      executions: t.executions ?? null,
    }));
    return { mfe: mfeCapture(rows), peak: peakCapture(rows, contracts) };
  }, [forPatterns, contracts]);

  /**
   * ── 🚨 GOALS IGNORE THE DATE FILTER AND KEEP THE ACCOUNT FILTER ──
   *
   * Every other card here measures the active range. A goal carries its own
   * period, and beta says why: "a daily $ target needs to measure today's P&L
   * even when the user has filtered the journal to last month." So this runs on
   * `goalsState.trades` — a separate read sized to the widest goal — and not on
   * `inScope` at all.
   *
   * ⚠ Dropping the DATE filter and dropping the ACCOUNT filter look like the
   * same simplification and are opposite decisions. The account selection is
   * still applied, twice over: `visibleGoals` decides which goals appear, and
   * `goalAccountScope` inside `goalProgress` decides which trades each one
   * counts. Sixth D77 surface and the first where one axis is dropped and the
   * other kept.
   */
  const goalRows = useMemo(() => {
    // ⚠ `journal` JOINS THE GUARD (#48). Two of the four templates are measured
    // over journal days, and rendering them before that read lands would show a
    // streak of 0 for one frame — which reads as "you broke your streak", not
    // as "still loading".
    if (!goalsState.goals || !goalsState.trades || !goalsState.journal
      || !accounts || !mode) return null;
    const nameOf = (id: string) =>
      (accounts as readonly Account[]).find((a) => a.id === id)?.name ?? null;
    const counts = goalMetricsGate(accounts as readonly Account[], selection);
    return visibleGoals(goalsState.goals, selection).map((g) => ({
      goal: g,
      chip: goalAccountLabel(g, nameOf),
      progress: goalProgress(g, goalsState.trades ?? [], {
        todayIso: goalsState.todayIso,
        mode,
        counts,
        activeAccountIds: selection,
        journal: goalsState.journal ?? [],
      }),
    }));
  }, [
    goalsState.goals, goalsState.trades, goalsState.journal, goalsState.todayIso,
    accounts, selection, mode,
  ]);

  /** The three breakdown cards. ⚠ NOT all over the same set — see above. */
  const breakdowns = useMemo(() => {
    if (!scoped || !forPatterns || !mode) return null;
    const shape = (t: TradeTableRow) => ({
      patternId: t.patternId,
      netPnl: t.netPnl,
      rrRealized: t.rrRealized,
      annotated: t.annotated,
      sessionLabel: t.sessionLabel,
      // ⚠ Resolved HERE with the day-boundary mode — `domain/` cannot reach one,
      // and reading the raw date is what put evening trades on the wrong day on
      // two earlier tabs.
      effectiveDate: effectiveTradeDate(t, mode),
    });
    // USD: the unit value IS net P&L, as on every other ported surface.
    const usd = (t: { netPnl?: number | null }) => t.netPnl ?? null;
    return {
      weekday: weekdayPnl(scoped.map(shape), usd),
      session: sessionPnl(scoped.map(shape), usd),
      // ⚠ `forPatterns`, NOT `scoped`. See the block above.
      patterns: topPatterns(
        forPatterns.map(shape),
        (patterns ?? []).map((p) => ({ id: p.id, name: p.name })),
        usd,
      ),
    };
  }, [scoped, forPatterns, mode, patterns]);

  if (ranged.phase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your dashboard</h3>
        <p className="muted">Nothing has been changed — this is a read problem. {ranged.error}</p>
      </div>
    );
  }

  if (!kpis || !recent || !mode) return <p className="muted">Loading your dashboard…</p>;

  return (
    <div className="dashboard-tab">
      {/* ── 🚨 THE CAPTURE REGION IS HERO → GOALS PROGRESS, AND STOPS THERE ──
          The old client's `#dash-snapshot-region` closes before the Activity
          divider: the heatmap and the Recent Trades table are deliberately OUT.
          A "performance report" someone shares is the summary, not the ledger —
          and Recent Trades carries account names and entry prices that have no
          business in an image posted to Discord. */}
      {/* ── THE COLD-START BANNERS (#46) ──
          Above the hero, because the whole point is that they are the first
          thing on a screen that otherwise offers a new user nothing to do.
          Both clear themselves; neither needs dismissing. */}
      {/* ⚠ THE GATE IS `!demo.active`, not merely the callback's presence. Inside
          a demo the book is full, so this banner cannot render anyway — but if
          it ever could, offering "load the Demo Account" from inside one is a
          button that appears to do nothing. */}
      <GetStarted
        accounts={accounts}
        universe={universe}
        go={go}
        onLoadDemo={demo.active ? undefined : demo.enter}
      />
      <Hero
        displayName={prefs?.displayName ?? ''}
        today={today}
        k={todayKpis}
        phase={todays.phase}
      />

      {/* ── 🔴 THE REGION OPENS HERE, AND IT USED TO OPEN ABOVE THE GREETING ──
          So a picture captioned "this month" led with "Good morning, Josh" and
          a single DAY's P&L, plus whichever cold-start banner was showing. #43.

          ⚠ THE COMMENT WAS COPIED FROM BETA AND THE MARKUP WAS NOT. Beta's own
          note calls the region "hero through Goals Progress", and by "hero" it
          means the hero GRID — Total P&L and the gauges. Its greeting row and
          Today's tile are emitted before the div opens (app/index.html:27944-
          27956), and the region starts one line later, at the Performance
          divider (:27960-27962). Read the markup, not the comment about it.

          🔴 AND THE SUITE CERTIFIED THE DIVERGENCE: a test named "the capture
          region is HERO through GOALS" asserted the greeting block was INSIDE
          it. Corrected in the same change, or the fix would not have landed. */}
      <div id={SNAPSHOT_REGION_ID}>
        {/* Rendered only during a capture, and by the region that owns it —
            never injected imperatively. See ui/state/snapshot.tsx. */}
        {snapshot.capturing === SNAPSHOT_REGION_ID ? (
          <div className="snap-title-bar">
            <h1>{prefs?.displayName?.trim() ? `${prefs.displayName.trim()} ` : ''}Performance Report</h1>
            <span className="mono">{captureLabel}</span>
          </div>
        ) : null}

      {/* ⚠ The old client's divider treatment — a small-caps pill with a rule
          fading to the right — not a plain `.section-header`. It groups the page
          into PERFORMANCE / BREAKDOWNS / ACTIVITY bands, and without it the
          sections read as an undifferentiated stack of cards.

          ⚠ NO RANGE LABEL ON THE RULE. It used to carry `{label}` on the right,
          which beta puts on no divider — and because this divider sits INSIDE
          the capture region, the exported picture printed the range twice in its
          top two rows: once in the title bar and again immediately underneath.
          The range is already stated in the title bar and in the Total P&L
          card's own sub-line. See #49. */}
      <div className="an-section-divider">
        <span className="an-label">Performance</span>
        <span className="an-rule" />
      </div>
      {chart ? (
        <div className="dash-hero-grid">
          <div className="card dash-eq">
            {/* ⚠ #41 — THE DISPLAYED TOTAL IS NOT `kpis.totalPnl`, AND MUST NOT
                BE. `kpis.totalPnl` feeds expectancy, profit factor and win rate;
                folding payouts into it would make a losing week read as
                profitable in every gauge on this screen. Only what is PRINTED
                moves, and the sub-line says by how much so the figure can still
                be reconciled against the Trades tab. */}
            <div className="dash-card-head">
              <h3>Total P&amp;L</h3>
              <span className="muted">
                {kpis.count} trades · active window
                {/* ⚠ NO MANUAL `+`. Beta writes one because its `fmtMoney` is
                    unsigned; this client's `money()` already prefixes the sign,
                    so adding one here prints `++$2,500.00`. Same string, two
                    different formatters — the kind of divergence that only
                    shows up in the rendered characters. */}
                {/* 🚨 AND ONLY IN DOLLARS. A payout has no points value, so
                    folding one into a points total would add dollars to points
                    and then print the sum as points. Beta’s own condition is
                    `unit === 'usd' && adjTotal` (index.html:27971). */}
                {shownAdj ? ` · incl. ${money(shownAdj)} adj.` : ''}
                {/* 🚨 THE MEANINGLESS-TOTAL WARNING, which nothing in this repo
                    rendered until now. `computeKpis` sets `mixed` when the unit
                    is points or ticks AND the trades span more than one
                    instrument root: ES points plus CL points is a sum with no
                    meaning. The flag has existed all along with no reader,
                    because every caller asked for dollars — where it is always
                    false. Beta’s wording and beta’s colour. */}
                {kpis.mixed ? (
                  <> · <span style={{ color: 'var(--gold)' }}>⚠ mixed instruments</span></>
                ) : null}
                {/* ⚠ AND THE POPULATION BEHIND THE MAGNITUDES. In R a stop-less
                    trade has no realised R; in ticks an uncatalogued contract
                    has no tick size. Those drop OUT of the total while still
                    counting in `kpis.count`, so the headline is a sum over fewer
                    trades than the caption claims. `domain/kpis` states the rule
                    outright: a caller showing a magnitude without showing
                    `valued` is quoting an average over an unstated population. */}
                {kpis.valued !== kpis.count ? (
                  <> · <span style={{ color: 'var(--gold)' }}>
                    {kpis.valued} of {kpis.count} priced in {UNIT_WORD[kpis.unit]}
                  </span></>
                ) : null}
              </span>
            </div>
            <div className={`dash-eq-total ${pnlClass(kpis.totalPnl + shownAdj)}`}>
              {unitValue(kpis.totalPnl + shownAdj, kpis.unit)}
            </div>
            {/* ── 🚨 THE CURVE IS STILL DOLLARS, AND IT SAYS SO ──

                Its points are built from `t.netPnl` (see `chart` above), which
                is the dollar figure whatever unit is chosen. Relabelling the
                axis with `unitValue` would put a "pts" suffix on dollar values
                — a wrong number under a right-looking label, which is worse
                than an unconverted chart.

                Converting it properly means the leg deltas arriving from a
                selector already in the unit, plus a rule for trades that have
                no value in it (a stop-less trade has no R, so it cannot step an
                R curve) and for adjustments (a payout has no points at all).
                That is real work, not a formatter swap — so until it lands the
                card NAMES the gap, the way Custom Analytics names its two
                unported dashboards rather than showing one under the other’s
                title. */}
            {kpis.unit === 'usd' ? null : (
              <p className="muted" style={{ fontSize: 11, margin: '0 0 4px' }}>
                Curve in dollars — {UNIT_WORD[kpis.unit]} not available on this
                chart yet.
              </p>
            )}
            <EquityCurve points={chart.equity} fmtY={(v) => money(v, 0)} />
          </div>

          <div className="card radial-stack">
            <GaugeCell
              label="Win Rate" g={chart.winRate} color="var(--blue)"
              fmt={(v) => pct(v, 0)}
              sub={`${kpis.wins}W / ${kpis.losses}L`}
            />
            <GaugeCell
              label="Risk-Adjusted Win Rate" g={chart.riskAdj} color="var(--blue)"
              fmt={(v) => pct(v, 0)}
              sub={`${Math.round((chart.riskAdj.value ?? 0) / 100 * chart.riskAdj.sample)} of ${chart.riskAdj.sample} trades ≥ 1R`}
            />
            <GaugeCell
              label="Expectancy (R)" g={chart.expR}
              // ⚠ Colour from the VALUE, arc length from `arcPct`. A negative
              // expectancy draws an empty arc AND turns red; using one number
              // for both would make -0.4R and 0.0R indistinguishable.
              color={(chart.expR.value ?? 0) >= 0 ? 'var(--blue)' : 'var(--red)'}
              fmt={(v) => `${num(v, 2)}R`}
              sub="avg R per trade"
            />
          </div>

          <div className="card radial-stack">
            <GaugeCell
              label="Discipline Score" g={chart.discipline} color="var(--purple)"
              fmt={(v) => pct(v, 0)}
              sub={chart.discipline.sample
                ? `${chart.discipline.sample} judged · +${chart.discipline.posTotal} / −${chart.discipline.negTotal}`
                : 'no judged trades yet'}
            />
            <GaugeCell
              label="Profit Factor" g={chart.pf} color="var(--blue)"
              // ⚠ `∞` is a real answer — winners and no losers — and the arc
              // fills. Rendering it as a number would make it sortable-looking.
              fmt={(v) => (v === Infinity ? '∞' : num(v, 2))}
              sub="$ won per $1 lost"
            />
            <GaugeCell
              label="Target Efficiency" g={chart.targetEff}
              color={(chart.targetEff.value ?? 0) >= 0 ? 'var(--blue)' : 'var(--red)'}
              // ⚠ The TEXT is unclamped where the arc is not, so a 140% runner
              // reads 140% on a full dial.
              fmt={(v) => `${num(v, 0)}%`}
              sub="% of planned target captured"
            />
          </div>
        </div>
      ) : null}

      <StatStrip k={kpis} capture={capture} />

      {breakdowns ? (
        <>
          <div className="an-section-divider">
            <span className="an-label">Breakdowns</span>
            <span className="an-rule" />
          </div>
          {/* ⚠ DOLLARS, LIKE THE TWO CHARTS. These rows are built from `netPnl`
              (see `breakdowns` above), so the same rule applies: say it rather
              than put a "pts" suffix on a dollar value. */}
          {kpis.unit === 'usd' ? null : (
            <p className="muted" style={{ fontSize: 11, margin: '0 0 6px' }}>
              Rows below in dollars — {UNIT_WORD[kpis.unit]} not available here yet.
            </p>
          )}
          <div className="dash-breakdowns">
            {/* ⚠ DOLLARS, STATED RATHER THAN INHERITED. These rows really are
                built from raw P&L and this tab already says so on screen a few
                lines below. Writing the literal makes that a decision at the
                call site instead of a default nobody chose. Whether the
                Dashboard should convert is its own parity question. */}
            <BarCard title="P&L by Weekday" rows={breakdowns.weekday} unit="usd" />
            <BarCard
              title="P&L by Session" rows={breakdowns.session}
              empty="No session-tagged trades yet."
              unit="usd"
            />
            <PatternCard rows={breakdowns.patterns} />
          </div>
        </>
      ) : null}

      <GoalsCard
        rows={goalRows}
        phase={goalsState.phase}
        error={goalsState.error}
      />
      </div>{/* /#dash-snapshot-region */}

      {/* ⚠ OUTSIDE the region, so the status line never appears in the PNG. */}
      {snapshot.message ? (
        <p
          className={`muted snap-status ${snapshot.phase === 'failed' ? 'red' : ''}`}
          role={snapshot.phase === 'failed' ? 'alert' : 'status'}
        >
          {snapshot.message}
        </p>
      ) : null}

      <div className="an-section-divider">
        <span className="an-label">Activity</span>
        <span className="an-rule" />
      </div>

      <section className="card pr-card">
        <div className="dash-card-head">
          <h3>Day × Hour Heatmap</h3>
          {/* ⚠ Dollars, for the same reason as the curve — `buildHeatmap` is
              handed `t.netPnl`. Named rather than mislabelled. */}
          <span className="muted">
            P&amp;L by session window
            {kpis && kpis.unit !== 'usd' ? ' · in dollars' : ''}
          </span>
        </div>
        {heatmap ? (
          <div className="heatmap-wrap">
            <Heatmap grid={heatmap} money={(n) => money(n, 0)} />
          </div>
        ) : (
          /* ⚠ Names the CAUSE, not just the absence. An imported trade with no
             time-in cannot be placed in an hour, and that is a fixable gap in the
             import rather than a broken card. */
          <p className="muted">
            No timestamped trades to plot. Imported trades need a non-empty time-in.
          </p>
        )}
      </section>
      {/* ⚠ THE HEADING IS INSIDE THE CARD, on the button row, as in beta
          (index.html:28038-28041). It used to sit above the card in a bare
          `.section-header`, which read as a page heading rather than as this
          card's own title. See #49. */}
      <section className="card">
        <div className="dash-card-head">
          <h3>Recent trades</h3>
          {/* ── ⚠ A VISIBLE DISABLED PLACEHOLDER, NOT AN ABSENCE ──
              Beta has a working `Coach Me — This Week` here. Rendering nothing
              made this the only unported control on the tab that is SILENT
              about it: Day Overview ships exactly this disabled placeholder for
              its own coach button, and the Accounts tab ships a working one, so
              a bare header read as "beta never had this" rather than "this is
              coming". A parity check has to be able to tell dropped from
              pending. See #45. */}
          {/* ⚠ `data-noshare`: beta marks this button too
              (app/index.html:28062). A Coach Me button in a published report is
              an invitation to spend somebody else's metered Claude run, on a
              page where it cannot be pressed at all. */}
          <button type="button" className="btn" disabled title="Not ported yet" data-noshare>
            Coach Me — This Week
          </button>
        </div>
      {/* ── 🚨 THE SAME TABLE AS THE TRADES TAB, NOT A SECOND ONE ──
          The old client calls `renderTradeTable(recent, 'recent')` here — its own
          renderer, unchanged. This tab had a hand-rolled six-column version
          (date / time / sym / side / qty / net P&L) against that one's eighteen,
          so account, entry, exit, points, R, MAE, MFE, pattern, grade and
          discipline were all absent from a glance that is supposed to show them.

          ⚠ Read-only: no `selection`, so no checkbox column and no sortable
          headers. Six rows whose meaning IS "the most recent six" must not offer
          to reorder themselves. */}
        {/* ── ⚠ THE RANGE WORDING IS KEPT, AND BETA’S IS NOT COPIED ──

            Beta prints "No trades yet. Use the dropzone in the Trades tab to
            import a CSV, or click + Add Trade." whenever this list is empty —
            including for a trader with a full book and a quiet week, where "no
            trades YET" is simply false.

            So the accurate sentence stays, and the instruction is added only
            when it is TRUE: the book itself is empty. That is the same
            whole-book signal the banners above use, for the same reason. */}
        {recent.length === 0 ? (
          <p className="muted">
            No trades in {label.toLowerCase()}.
            {universe && universe.length === 0
              ? ' Import a CSV or add your first trade from the Trades tab.'
              : ''}
          </p>
        ) : (
          /* ── ⚠ NO `onOpen`, AND THAT IS NOW A REAL STATE RATHER THAN A STUB ──
             The trade form is not wired here yet — Trades owns it, and opening
             it from two tabs needs the reload path threading through both.

             This used to pass an EMPTY handler, which left every row styled
             `clickable` and keyboard-focusable: the pointer became a hand, the
             row took focus and highlighted, and clicking or pressing Enter did
             nothing at all. An inert row that advertises itself is worse than a
             plainly inert one. `TradeTable` now drops the affordance when no
             handler is supplied, so the row looks like what it is. See #45. */
          <TradeTable
            unit={unit}
            valueOf={valueOf}
            rows={recent}
            kind="annotated"
            mode={mode}
            labelAccount={labelAccount}
            /* ⚠ THE DEFAULT SET, NOT THE TRADES TAB'S ACCOUNTING ONE (#45).
               This strip exists for the four REVIEW fields — which setup, what
               grade, how disciplined, which tags. It was rendering the
               accounting layout instead: those four absent, and Gross P&L and
               Commissions present, which beta deliberately keeps off this
               screen. Same component, different layout, exactly as beta keeps
               two column lists. */
            columns={TRADE_TABLE_COLUMNS_DEFAULT}
            labelPattern={labelPattern}
          />
        )}
      </section>

      <NotPortedYet />
    </div>
  );
}

/**
 * Greeting, date, and Today's P&L.
 *
 * ⚠ `k` is from the TODAY read, not the range read. See the file header — the
 * two are different questions and feeding this one from the range would show $0
 * whenever the filter excludes today.
 */
function Hero(
  { displayName, today, k, phase }:
  {
    displayName: string;
    today: string | null;
    k: Kpis<TradeTableRow> | null;
    /** ⚠ The read's PHASE, not a boolean. `loading` and `failed` need different
        captions, and collapsing them is what produced "0 trades today" on a
        dropped connection. */
    phase: 'loading' | 'ready' | 'failed';
  },
) {
  /** Only a read that actually came back may print a number OR a count. */
  const settled = phase === 'ready' && k != null;
  // Time-of-day greeting, matching the old client's cutoffs (12 / 17).
  const hour = new Date().getHours();
  const part = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const name = displayName.trim();

  const pnl = k?.totalPnl ?? null;
  const n = k?.count ?? 0;

  return (
    <div className="dash-hero">
      <div>
        <h2 className="dash-greeting">{part}{name ? `, ${name}` : ''}</h2>
        {/* ⚠ Beta's format — `Saturday, August 1, 2026`, not a bare ISO date.
            The VALUE is still the SESSION day, which is the same anchor the range
            uses, so this line and the filter can never disagree about what
            "today" is. See `fullDayLabel` for where the two clients diverge and
            why this one is right. */}
        <div className="muted">{today ? fullDayLabel(today) : '—'}</div>
      </div>
      {/* ⚠ The tile GLOWS by sign — beta's `glow-green` / `glow-red`
          (index.html:27929). The classes were already in components.css and the
          Calendar tab already used them; this tab simply never applied them,
          dropping the strongest at-a-glance signal on the landing screen. A flat
          day glows neither way, as in beta. See #49. */}
      <div className={`kpi dash-today ${settled && pnl != null && pnl > 0 ? 'glow-green' : settled && pnl != null && pnl < 0 ? 'glow-red' : ''}`}>
        <div className="kpi-label">TODAY’S P&amp;L</div>
        {/* ⚠ Pending is not zero. "$0.00" is a real, meaningful answer — a flat
            day — so it must never stand in for "not loaded yet". */}
        <div className={`kpi-value ${settled ? pnlClass(pnl) : ''}`}>
          {/* 🚨 `k.unit`, NOT `money()`. `todayKpis` is computed in the chosen
              unit, so formatting it as dollars printed 25 points as "+$50.00"
              — a dollar sign in front of a points figure, on the first number
              of the landing screen. Caught by a test, not by reading. */}
          {settled ? unitValue(pnl, k?.unit ?? 'usd') : '—'}
        </div>
        {/* ⚠ 🚨 AND A FAILED READ IS NOT A ZERO EITHER, WHICH THIS LINE USED TO
            CLAIM. The value above already refused to invent a number on a failed
            read — but the caption fell through to `${n} trades today` with n = 0,
            so a dropped connection printed `—` over the words "0 trades today".
            The number was honest and the sentence under it was a confident false
            statement. Both halves now key off the same `settled`. See #42. */}
        <div className="kpi-sub muted">
          {phase === 'failed'
            ? 'couldn’t load today'
            : !settled
              ? 'loading…'
              : `${n} trade${n === 1 ? '' : 's'} today`}
        </div>
      </div>
    </div>
  );
}

/**
 * One gauge, its label and its sub-line.
 *
 * ⚠ A NULL value renders `—` and an EMPTY arc, never `0%`. "No trades with a
 * planned R" and "you captured 0% of your targets" are different statements and
 * the second is an accusation — the same call the Prop Report makes about ROI.
 */
function GaugeCell(
  { label, g, color, fmt, sub }:
  {
    label: string;
    g: GaugeData;
    color: string;
    fmt: (v: number) => string;
    sub: string;
  },
) {
  return (
    <div className="radial-cell">
      <div className="rlabel">{label}</div>
      <div className="rgauge">
        <Gauge
          pct={g.value == null ? 0 : g.arcPct}
          text={g.value == null ? '—' : fmt(g.value)}
          color={g.value == null ? 'var(--text-3)' : color}
          label={label}
        />
      </div>
      {/* ⚠ THE CAPTION IS ALWAYS THE CALLER'S, as in beta — which renders every
          `rsub` unconditionally.

          This used to read `{has || g.value === Infinity ? sub : 'no data yet'}`,
          throwing the caption away in exactly the case it was written for: the
          Discipline dial passes `no judged trades yet` when nothing has been
          graded, and the user got the generic `no data yet` instead — losing the
          one sentence that says the fix is to go and grade some trades.

          ⚠ The null-vs-zero rule this override was reaching for lives in the
          VALUE above (`—`, never `0%`), not in the caption. A caption reading
          "avg R per trade" under a `—` is beta's own rendering and is honest;
          suppressing it was not. See #42. */}
      <div className="rsub muted">{sub}</div>
    </div>
  );
}

/**
 * The SECONDARY STAT STRIP — eight cells, one row, and the eight are beta's.
 *
 * ── 🚨 THIS USED TO BE A DIFFERENT EIGHT, AND THAT WAS THE DEFECT ──
 *
 * It was written before the gauge row landed and never revisited, so it carried
 * Net P&L, Trades, Win Rate, Profit Factor, Expectancy and Streaks. Four of those
 * are shown twice on the same screen once the gauges exist — Win Rate and Profit
 * Factor as dials directly above, Net P&L and the trade count in the Total P&L
 * card's heading — and two (dollar Expectancy, Streaks) are not on beta's
 * dashboard at all.
 *
 * Adding the capture pair is what surfaced it: ten cells in a
 * `repeat(auto-fit, minmax(150px, 1fr))` grid wrap 8 + 2, leaving a ragged half
 * row. The wrap was the symptom; the duplication was the fault.
 *
 * ⚠ So the rule for this strip is now the same as everywhere else in the port:
 * it is beta's, cell for cell, in beta's order. Nothing gets added here because
 * it seems useful — it gets added because beta has it.
 */
function StatStrip(
  { k, capture }:
  { k: Kpis<TradeTableRow>; capture: { mfe: CaptureStat; peak: CaptureStat } | null },
) {
  const setups = (n: number) => `${n} setup${n === 1 ? '' : 's'}`;
  // ⚠ `t.date`, not the effective date. Beta labels these with `core.date`, and
  // an evening trade would otherwise be attributed to the next session day in a
  // caption whose whole job is to let you find the trade in the table.
  const when = (t: TradeTableRow | null) =>
    t ? `${t.contract || '—'} ${t.date ?? ''}`.trim() : '—';

  return (
    <div className="trade-stats-strip" data-testid="dash-kpis">
      <Stat label="Avg Win" value={unitValue(k.avgWin, k.unit)} cls="green" />
      <Stat label="Avg Loss" value={unitValue(k.avgLoss, k.unit)} cls="red" />
      {/* ⚠ No colour. A negative average R:R is bad news and beta still renders
          it in the default ink — the sign carries it, and colouring a ratio the
          way money is coloured implies a dollar reading it does not have. */}
      <Stat label="Avg R:R" value={num(k.avgRR, 2)} />

      {/* ── The capture pair. Read together, and MEANT to disagree. ──
          ⚠ Green above zero, red below, and the sign is the whole reading: a
          negative capture is a trade that ran a full R in your favour and closed
          red. `—` when nothing qualifies, never 0% — "no trade offered a full R"
          and "you kept none of what was offered" are different statements and
          the second is an accusation. */}
      <Stat
        label="MFE Capture"
        value={capture?.mfe.pct == null ? '—' : `${num(capture.mfe.pct, 0)}%`}
        cls={capture?.mfe.pct == null ? '' : capture.mfe.pct >= 0 ? 'green' : 'red'}
        sub={capture?.mfe.n ? `${setups(capture.mfe.n)} ≥1R MFE` : 'no ≥1R MFE trades'}
      />
      <Stat
        label="Peak Capture"
        value={capture?.peak.pct == null ? '—' : `${num(capture.peak.pct, 0)}%`}
        cls={capture?.peak.pct == null ? '' : capture.peak.pct >= 0 ? 'green' : 'red'}
        // ⚠ The sub-line names the BASIS, not just the count, because the count
        // is legitimately smaller than the tile above it: a manual trade has no
        // fill timeline. Without "size-at-peak" the gap reads as a data fault.
        sub={capture?.peak.n ? `size-at-peak · ${setups(capture.peak.n)}` : 'no fill timeline'}
        title={
          'Size-at-peak-weighted capture: graded only on the contracts that actually rode '
          + 'through the MFE peak, each against the full MFE. Scale-outs you booked BEFORE '
          + 'the peak aren’t graded against a high they never had a shot at — unlike MFE '
          + 'Capture (blended VWAP ÷ full MFE). A big gap above MFE Capture means early '
          + 'scale-outs are dragging the standard number down.'
        }
      />

      <Stat
        label="Best Trade"
        // ⚠ `bestValue`, NOT `bestTrade.netPnl`. The ranking is done on the
        // display-unit value, but the trade object’s own `netPnl` is always
        // dollars — reading it would name the right trade and print the wrong
        // number beside it.
        value={unitValue(k.bestValue, k.unit)}
        cls="green" sub={when(k.bestTrade)}
      />
      <Stat
        label="Worst Trade"
        value={unitValue(k.worstValue, k.unit)}
        cls="red" sub={when(k.worstTrade)}
      />
      {/* ⚠ No sub-line, deliberately: beta has none here, and the strip's last
          cell carrying a caption the seven before it lack reads as a stray. */}
      <Stat label="Max Drawdown" value={unitValue(-Math.abs(k.maxDrawdown), k.unit)} cls="red" />
    </div>
  );
}

function Stat(
  { label, value, sub, cls, title }:
  { label: string; value: string; sub?: string; cls?: string; title?: string },
) {
  return (
    <div className="stat-cell" title={title}>
      <div className="slabel">{label}</div>
      <div className={`sval ${cls ?? ''}`}>{value}</div>
      {sub ? <div className="ssub">{sub}</div> : null}
    </div>
  );
}

interface GoalRow {
  goal: Goal;
  chip: string;
  progress: GoalProgress;
}

/**
 * Goals Progress — period targets, page-wide, between Breakdowns and Activity.
 *
 * ⚠ THE BAR IS CLAMPED AND THE TEXT IS NOT. A week at −$5,852.95 against a
 * +$1,500 target is −390%: the bar sits empty and the numbers say exactly what
 * happened. Clamping the text too would print "0%" over a losing week, which is
 * the one reading a target card must never give.
 */
function GoalsCard(
  { rows, phase, error }:
  { rows: GoalRow[] | null; phase: 'loading' | 'ready' | 'failed'; error: string | null },
) {
  return (
    <section className="card goals-card" data-testid="dash-goals">
      <div className="dash-card-head">
        <h3>Goals Progress</h3>
        <span className="muted">period targets</span>
      </div>

      {phase === 'failed' ? (
        // ⚠ A failed read is NOT "no goals defined". One is a problem with the
        // software and the other is an instruction to go and set some.
        <p className="muted" role="alert">Couldn’t load your goals. {error}</p>
      ) : !rows ? (
        <p className="muted">Loading your goals…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No goals defined. Add some in Settings.</p>
      ) : (
        rows.map(({ goal, chip, progress }) => (
          <div className="goal-row" key={goal.id}>
            <div className="goal-head">
              <span className="goal-name">
                {goal.name}
                {' '}
                <span className="muted">· {goal.template} · {goal.period}</span>
                {' '}
                <span className="tag">{chip}</span>
              </span>
              {progress.supported ? (
                /* ⚠ THE DOMAIN PHRASES THREE OF THE FOUR (#48). This read
                   `money(actual) / money(target)` for every template, which is
                   right for exactly one of them — an adherence percentage, a
                   tag COUNT and a number of DAYS are not dollars. `dollar-pl`
                   returns empty labels precisely because it is the one whose
                   unit the renderer owns. */
                <span className={`mono ${progress.pct >= 100 ? 'green' : ''}`}>
                  {progress.label
                    ? `${progress.label} / ${progress.targetLabel}`
                    : `${money(progress.actual)} / ${money(progress.target)}`}
                </span>
              ) : (
                // ⚠ Named, not blank and not zero. Reached now only by a
                // template this build has never heard of — a goal written by a
                // newer client, or a hand-edited row.
                <span className="muted">not tracked here yet</span>
              )}
            </div>
            <div className="progress">
              <div
                className={`progress-fill ${progress.pct >= 100 ? 'over' : ''}`}
                style={{
                  width: progress.supported
                    ? `${Math.max(0, Math.min(100, progress.pct))}%`
                    : '0%',
                }}
              />
            </div>
          </div>
        ))
      )}
    </section>
  );
}

/**
 * Top Patterns, ranked by expectancy.
 *
 * ⚠ THE SAMPLE IS RENDERED BESIDE EVERY ROW, and that is not decoration: the
 * ranking is by expectancy, so a single trade can head the list. Without the
 * count the card reads as "your best setup" when it may mean "your only trade".
 */
function PatternCard({ rows }: { rows: TopPatternRow[] }) {
  const max = rows.reduce((m, r) => Math.max(m, Math.abs(r.expectancy)), 0);
  return (
    <div className="card">
      <div className="dash-card-head">
        <h3>Top Patterns</h3>
        <span className="muted">by expectancy</span>
      </div>
      {rows.length === 0 ? (
        <p className="muted">No tagged trades in this window.</p>
      ) : (
        <div className="mini-bars">
          {rows.map((r) => (
            <div className="mini-bar-row" key={r.id}>
              <span className="mini-bar-k" title={`${r.name} · ${r.sample} trades`}>{r.name}</span>
              <span className="mini-bar-track">
                <span
                  className={`mini-bar-fill ${r.expectancy >= 0 ? 'up' : 'down'}`}
                  style={{ width: max ? `${(Math.abs(r.expectancy) / max) * 100}%` : '0%' }}
                />
              </span>
              <span className={`mini-bar-v ${pnlClass(r.expectancy)}`}>{money(r.expectancy)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * ⚠ Named on screen rather than silently absent.
 *
 * A dashboard missing half its panels looks finished if nothing says otherwise,
 * and the person checking parity against the live app is exactly the one who
 * needs to tell "not ported" from "broken".
 */
function NotPortedYet() {
  return (
    <div className="card">
      <h3>Not on this screen yet</h3>
      {/* ── 🚨 THIS LIST MUST SHRINK IN THE SAME COMMIT AS THE CARD THAT LANDS ──
          It has now rotted TWICE. First it named the equity curve and the six
          gauges under a rendered equity curve and six gauges; the fix replaced
          the test that was pinning those words with a DOM-fact invariant — and
          the very next commit shipped the Day × Hour heatmap and left it named
          here anyway, because the invariant only knew about the panels that had
          already burned us.

          So the invariant in Dashboard.test.tsx no longer holds a list at all:
          it reads every heading and stat label ON THE PAGE and fails if this
          card names any of them. Adding a panel with a heading is now what
          breaks the test, which is the only version of this rule that survives
          someone who has not read this comment. */}
      {/* ⚠ The invariant below CAUGHT this list on its first real outing: Goals
          Progress landed and this sentence still named it, and the test failed
          by name without anyone remembering to look. That is the difference
          between D84's page-derived rule and the panel list it replaced.

          ── 🚨 AND IT ROTTED A THIRD TIME ANYWAY, IN THE OTHER DIRECTION ──

          This card spent weeks saying the snapshot camera was "missing, not
          broken" while the camera was mounted in the toolbar and WORKING — this
          very file registers its capture target and draws its title bar. The
          invariant could not see it: it collects `<h3>` headings and `.slabel`
          stat labels, and "the snapshot camera" is neither. It is prose in this
          card's own body, which is the one place the rule does not look.

          🔴 Worse, a test REQUIRED the false sentence
          (`Dashboard.test.tsx`, "names the unported panels on screen"), so
          deleting the lie turned the suite red. Second time a test has pinned
          this card's rot in place.

          ▶ The rule that survives: this card names a gap only when something on
          this page can be pointed at as its absence. The camera did not qualify.

          ── 🔴 AND IT ROTTED A FOURTH TIME, THE MOMENT #41 LANDED ──

          This card named account adjustments — "a prop-firm payout will not show
          up" — and a test at `honesty about what is missing` REQUIRED that
          sentence. Shipping the adjustments fix made the copy false and would
          have left it on screen, under a headline that now includes the very
          payout it said was missing. THIRD time a test has pinned this card's
          rot, and the first where the rot was created by FIXING the thing.

          ▶ So the standing instruction is not "keep the list short". It is:
          **the commit that closes a gap edits this card and its test in the same
          breath.** Nothing else has worked here.

          ▶ THE DISPLAY UNIT WAS WHAT THIS NAMED, AND IT NOW HALF-EXISTS, so the
          card had to change with it rather than keep claiming the whole thing is
          missing. The toolbar control is real, the preference is shared with beta,
          and the headline total, the eight stat tiles and the goal rows all follow
          it. What is left on THIS screen is the two chart surfaces: the equity
          curve and the heatmap are still built from dollar deltas, so they render
          in dollars and SAY SO in place rather than putting a "pts" suffix on a
          dollar value.

          ⚠ Converting them is not a formatter swap: the leg and cell values have
          to arrive from a selector already in the unit, and that needs a rule for
          trades with no value in it (a stop-less trade cannot step an R curve) and
          for adjustments (a payout has no points at all). Tracked in #44. */}
      {/* ⚠ NAMES NO PANEL, and that is a constraint rather than a style choice:
          an invariant test above forbids this card from containing the text of
          any heading or stat label the tab renders. The first wording named the
          two charts outright and went red — correctly, because the charts are on
          screen. What is missing is their UNIT SUPPORT, so that is what this
          says. */}
      <p className="muted">
        Points, ticks and R on the two charts here. The toolbar’s unit switch
        drives every figure on this screen, but both charts are still drawn from
        dollar values — each says so where it sits.
      </p>
    </div>
  );
}
